import {PolymerElement} from '@polymer/polymer/polymer-element.js';
import {html} from '@polymer/polymer/lib/utils/html-tag.js';
import '../ns-form/ns-form.js';
import {ZIP_TRANSLATIONS} from './zip-translations.js';
import {ZIP_CONFIG} from './zip-config.js';
import template from './ns-zip.html';
import {FormMixin} from '../formMixin.js';

/**
 * The `ns-zip` component is an input element that takes in a region's postal code.
 * It inherits properties, validation functionality, and styles from `ns-input`.
 *
 * <section>
 *  <h2>Content Guidelines</h2>
 *  <p>Here are some guidelines on how to properly use this element.</p>
 * </section>
 *
 * <h1>Zip Component Demo</h1>
 * <demo-snippet class="demoSnippet">
 *   <template>
 *     <ns-zip></ns-zip>
 *   </template>
 * </demo-snippet>
 */
export class NsZip extends FormMixin(PolymerElement) {
    static get is() {
        return 'ns-zip';
    }

    static get template() {
        let htmlElement = document.createElement('TEMPLATE');
        htmlElement.innerHTML = template;
        return htmlElement;
    }

    static get properties() {
        return {
            /**
             * ISO country code
             * Input value
             */
            country: {
                type: String,
                value: "US",
                reflectToAttribute: true,
                required: true,
                observer: '_initialize'
            },
            /**
             * Label for the zip input
             */
            label: {
                type: String,
                value: "Zip Code",
                reflectToAttribute: true,
            },
            /**
             * ISO language code
             * Required property
             */
            language: {
                type: String,
                value: "en",
                reflectToAttribute: true,
                required: true,
                observer: '_initialize'
            },
            /**
             * Regex validation
             */
            regex: {
                type: String
            },
            /**
             * Max length
             */
            maxLength: {
                type: Number
            },
            /**
             * Received binding from top components for zip
             */
            zip: {
                type: String,
                notify: true,
                reflectToAttribute: true
            },
            /**
             * Required attribute activates Regex Validation
             */
            required: {
                type: Boolean
            },
            /**
             * Flag representing the dirty state of this component
             */
            dirty: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true
            }
        };
    }

    static get observers() {
        return [
            '_updateDirtyFlag(zipDirty)'
        ];
    }

    /**
     * Polymer lifecycle function.
     * If country and language are not set, it will set to default country and language.
     * Consequently on this previous condition, it will set required flag to false and skip
     * assigning a regex for validation
     */
    ready() {
        super.ready();
    }

    _initialize() {
        this._localize(this.language, this.country, ZIP_TRANSLATIONS);
        this._setConfig(this.country, ZIP_CONFIG);
    }

    /**
     * Form validation - ns-form looks for this function for validation
     */
    validate() {
        return this.shadowRoot.querySelector("ns-form").validate();
    }
}

customElements.define(NsZip.is, NsZip);
